import React from "react";
import { Box, Button, Tooltip } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";

// Attached to each listings row by the listingsTable lambda on every read
export interface RelatedListings {
  equivalent_count: number;
  equivalent_inventory_ids: number[];
  linked_count: number;
  linked_asins: string[];
  linked_inventory_ids: number[];
}

const PRICETOOL_PATH = "/datatables/pricetool";

const BUTTON_SX = {
  minWidth: 0,
  px: 0.75,
  py: 0,
  lineHeight: 1.6,
  whiteSpace: "nowrap",
} as const;

// Carry the current column set into the new tab, like the toolbar's Open actions
const createBaseParams = () => {
  const params = new URLSearchParams();
  params.set("tab", "all");
  const colset = new URLSearchParams(window.location.search).get("colset");
  if (colset) params.set("colset", colset);
  return params;
};

// inventory_id is a positive auto-increment, so this matches nothing. An empty
// isAnyOf is dropped by both the grid and the server, which would show every
// row instead of none.
const NO_INVENTORY_SENTINEL = "-1";

// Both buttons show the inventory behind the listings they open. Values are
// strings because Supply Inventory ID is a string column in the grid.
const setInventoryParams = (
  params: URLSearchParams,
  inventoryIds: number[] | undefined
) => {
  const values = inventoryIds?.length
    ? inventoryIds.map(String)
    : [NO_INVENTORY_SENTINEL];
  params.set("inventory_filter_supply.inventory_id_operator", "isAnyOf");
  params.set(
    "inventory_filter_supply.inventory_id_value",
    JSON.stringify(values)
  );
  params.set("inventory_sort_supply.sort", "asc");
};

// Sort params are read back in insertion order, so their order here is the
// sort priority in the new tab.
export const buildEquivalentsUrl = (
  productId: number,
  inventoryIds: number[] | undefined
): string => {
  const params = createBaseParams();
  params.set("listings_filter_product.product_id_operator", "=");
  params.set("listings_filter_product.product_id_value", String(productId));
  params.set("listings_sort_amazon_listings.sort", "asc");
  setInventoryParams(params, inventoryIds);
  return `${PRICETOOL_PATH}?${params.toString()}`;
};

export const buildInventoryLinkedUrl = (
  asins: string[],
  inventoryIds: number[] | undefined
): string => {
  const params = createBaseParams();
  params.set("listings_filter_amazon_listings.asin_operator", "isAnyOf");
  params.set(
    "listings_filter_amazon_listings.asin_value",
    JSON.stringify(asins)
  );
  params.set("listings_sort_product.product_id", "asc");
  params.set("listings_sort_amazon_listings.sort", "asc");
  setInventoryParams(params, inventoryIds);
  return `${PRICETOOL_PATH}?${params.toString()}`;
};

// Keep clicks from selecting the row or starting an edit
const stopPropagation = (event: React.SyntheticEvent) =>
  event.stopPropagation();

export function RelatedListingsCell({
  productId,
  related,
}: {
  productId?: number | null;
  related?: RelatedListings | null;
}) {
  if (productId === null || productId === undefined || !related) return null;

  const {
    equivalent_count,
    equivalent_inventory_ids,
    linked_count,
    linked_asins,
    linked_inventory_ids,
  } = related;

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 0.5, height: "100%" }}
    >
      <Tooltip
        title={`Equivalents: ${equivalent_count} listing${
          equivalent_count === 1 ? "" : "s"
        } for product ${productId}`}
      >
        <Button
          size="small"
          variant="outlined"
          href={buildEquivalentsUrl(productId, equivalent_inventory_ids)}
          target="_blank"
          rel="noreferrer"
          onClick={stopPropagation}
          onDoubleClick={stopPropagation}
          sx={BUTTON_SX}
        >
          = {equivalent_count}
        </Button>
      </Tooltip>
      {linked_count > 0 && (
        <Tooltip
          title={`Inventory Linked Listings: ${linked_count} listing${
            linked_count === 1 ? "" : "s"
          } share inventory with product ${productId}`}
        >
          <Button
            size="small"
            variant="outlined"
            href={buildInventoryLinkedUrl(linked_asins, linked_inventory_ids)}
            target="_blank"
            rel="noreferrer"
            onClick={stopPropagation}
            onDoubleClick={stopPropagation}
            startIcon={<LinkIcon />}
            sx={{ ...BUTTON_SX, "& .MuiButton-startIcon": { mr: 0.25 } }}
          >
            {linked_count}
          </Button>
        </Tooltip>
      )}
    </Box>
  );
}
